import React, { useState, useEffect, useCallback } from 'react';
import { Send, X, AlertTriangle, ShieldCheck, CheckCircle2, XCircle, RefreshCw, Info } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import { previewTransfer, transferToSap, checkTransfer, checkUpdate, updateInSap } from './materialApi';

/**
 * TransferDialog — zwei Betriebsarten, die der Server mit der Vorschau vorgibt (preview.op):
 *   create  (status ready): erzeugtes BAPI_MATERIAL_SAVEDATA-Payload, „In SAP prüfen" (Trockenlauf:
 *           BAPI-Aufruf + Rollback, nichts angelegt), „An SAP übertragen".
 *   change  (status transferred): Delta gegen den letzten SAP-Stand (nur geänderte Felder),
 *           „Änderungen in SAP prüfen" (Trockenlauf ohne Nummernverbrauch), „In SAP aktualisieren".
 * Welche Umgebung dahinter liegt (mock/sap/none), meldet der Server mit (target).
 *
 * Props: user, materialId, materialLabel, onDone(), onClose(), showSuccess, showError
 */
const TransferDialog = ({ user, materialId, materialLabel, onDone, onClose, showSuccess, showError }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [preview, setPreview] = useState(null); // { op, payload, target, changes?, hasChanges?, matnr?, baseline? }
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [check, setCheck] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await previewTransfer(user, materialId);
      setPreview({ op: res.op || 'create', payload: res.payload || null, target: res.target || 'mock',
        changes: Array.isArray(res.changes) ? res.changes : [], hasChanges: !!res.hasChanges, matnr: res.matnr || null, baseline: res.baseline || null });
    } catch { showError?.(t('material.trfPreviewError')); }
    finally { setLoading(false); }
  }, [user, materialId, showError, t]);
  useEffect(() => { load(); }, [load]);

  const isChange = preview?.op === 'change';
  const target = preview?.target || 'mock';
  const payload = preview?.payload;

  const doCheck = async () => {
    setChecking(true);
    setCheck(null);
    try {
      const res = isChange ? await checkUpdate(user, materialId) : await checkTransfer(user, materialId);
      setCheck(res);
      if (res?.ok) showSuccess?.(t(isChange ? 'material.updCheckOk' : 'material.trfCheckOk'));
      else showError?.(t(isChange ? 'material.updCheckFail' : 'material.trfCheckFail'));
    } catch (e) { showError?.(e.message || t('material.trfError')); }
    finally { setChecking(false); }
  };

  const doTransfer = async () => {
    setBusy(true);
    try {
      const res = isChange ? await updateInSap(user, materialId) : await transferToSap(user, materialId);
      if (res?.ok === false) {
        setCheck(res);
        showError?.(res.message || t('material.trfError'));
        return;
      }
      if (isChange) {
        showSuccess?.(res?.noChanges ? t('material.updNoChanges') : `${t('material.updDone')} ${t('material.trfMatnr')}: ${res?.matnr || preview?.matnr || ''}`);
      } else {
        showSuccess?.(res?.matnr ? `${t('material.trfDone')} ${t('material.trfMatnr')}: ${res.matnr}` : (res?.message || t('material.trfDone')));
      }
      onDone?.();
    } catch (e) { showError?.(e.message || t('material.trfError')); }
    finally { setBusy(false); }
  };

  const noteText = target === 'sap' ? t(isChange ? 'material.updTargetSap' : 'material.trfTargetSap')
    : target === 'none' ? t('material.trfTargetNone') : t('material.trfStubNote');
  const noteClass = target === 'sap'
    ? (isDarkMode ? 'bg-emerald-500/10 text-emerald-200' : 'bg-emerald-50 text-emerald-700')
    : (isDarkMode ? 'bg-amber-500/10 text-amber-200' : 'bg-amber-50 text-amber-700');

  const panel = isDarkMode ? 'bg-gray-900 border border-white/10' : 'bg-white border border-slate-200';
  const sectionTitle = isDarkMode ? 'text-white/80' : 'text-slate-700';
  const kvBox = isDarkMode ? 'bg-white/5' : 'bg-slate-50';
  const muted = isDarkMode ? 'text-white/50' : 'text-slate-400';

  // Daten-Strukturen (X-Strukturen technisch ausgeblendet)
  const DATA_STRUCTS = ['HEADDATA', 'CLIENTDATA', 'PLANTDATA', 'VALUATIONDATA', 'SALESDATA', 'STORAGELOCATIONDATA'];

  const renderKV = (obj) => (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1">
      {Object.entries(obj).map(([k, v]) => (
        <div key={k} className="flex flex-col">
          <span className={`font-mono text-[10px] ${isDarkMode ? 'text-white/35' : 'text-slate-400'}`}>{k}</span>
          <span className={`text-sm ${isDarkMode ? 'text-white/85' : 'text-slate-700'}`}>{String(v)}</span>
        </div>
      ))}
    </div>
  );

  const changesPending = isChange && preview && !preview.hasChanges;
  const canWrite = !busy && !checking && !loading && target !== 'none' && !changesPending && !(isChange && !preview?.matnr);
  const title = isChange ? t('material.updTitle') : t('material.trfTitle');

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onMouseDown={onClose}>
      <div className={`w-full max-w-2xl rounded-2xl shadow-2xl ${panel} flex flex-col max-h-[85vh]`} onMouseDown={(e) => e.stopPropagation()}>
        {/* Kopf */}
        <div className={`flex items-start gap-3 p-5 border-b ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`}>
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-blue-500/15' : 'bg-blue-50'}`}>
            {isChange ? <RefreshCw className="w-5 h-5 text-blue-500" /> : <Send className="w-5 h-5 text-blue-500" />}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{title}</h3>
            <p className={`text-xs mt-0.5 truncate ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
              {materialLabel}{isChange && preview?.matnr ? ` · ${preview.matnr}` : ''} · BAPI_MATERIAL_SAVEDATA{isChange ? ' (change)' : ''}
            </p>
          </div>
          <button type="button" onClick={onClose} className={`p-1 rounded-lg ${isDarkMode ? 'text-white/50 hover:bg-white/10' : 'text-slate-400 hover:bg-slate-100'}`}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Ziel-Hinweis: mock / sap / none — kommt vom Server mit dem Payload */}
        <div className={`mx-5 mt-4 rounded-xl px-3 py-2 text-xs flex items-start gap-2 ${noteClass}`}>
          {target === 'sap' ? <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" /> : <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />}
          <span>{noteText}</span>
        </div>

        {/* Change-Pfad ohne Vergleichsstand / ohne Aenderungen */}
        {isChange && !loading && !preview?.baseline && (
          <div className={`mx-5 mt-3 rounded-xl px-3 py-2 text-xs flex items-start gap-2 ${isDarkMode ? 'bg-red-500/10 text-red-200' : 'bg-red-50 text-red-800'}`}>
            <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /><span>{t('material.updNoBaseline')}</span>
          </div>
        )}
        {changesPending && preview?.baseline && (
          <div className={`mx-5 mt-3 rounded-xl px-3 py-2 text-xs flex items-start gap-2 ${isDarkMode ? 'bg-white/5 text-white/70' : 'bg-slate-50 text-slate-600'}`}>
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" /><span>{t('material.updNoChanges')}</span>
          </div>
        )}

        {/* Ergebnis der SAP-Pruefung bzw. einer abgelehnten Uebertragung */}
        {check && (
          <div className={`mx-5 mt-3 rounded-xl px-3 py-2 text-xs ${check.ok ? (isDarkMode ? 'bg-emerald-500/10 text-emerald-200' : 'bg-emerald-50 text-emerald-800') : (isDarkMode ? 'bg-red-500/10 text-red-200' : 'bg-red-50 text-red-800')}`}>
            <div className="flex items-center gap-2 font-semibold">
              {check.ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
              <span>{check.ok ? t(isChange ? 'material.updCheckOk' : 'material.trfCheckOk') : t(isChange ? 'material.updCheckFail' : 'material.trfCheckFail')}</span>
            </div>
            {check.matnr && (
              <div className="mt-1">{t('material.trfMatnr')}: <span className="font-mono">{check.matnr}</span>
                {check.numberAssignment && <span className="opacity-70"> ({check.numberAssignment === 'internal' ? t('material.trfNumberInternal') : t('material.trfNumberExternal')})</span>}
              </div>
            )}
            {Array.isArray(check.views) && check.views.length > 0 && (
              <div className="mt-0.5 opacity-80">{t('material.trfViews')}: {check.views.join(', ')}</div>
            )}
            {Array.isArray(check.messages) && check.messages.length > 0 && (
              <div className="mt-1.5">
                <div className="font-semibold">{t('material.trfMessages')}</div>
                <ul className="mt-0.5 space-y-0.5">
                  {check.messages.map((m, i) => (
                    <li key={i} className="font-mono text-[11px]">[{m.type}] {m.id} {m.number}: {m.message}</li>
                  ))}
                </ul>
              </div>
            )}
            {!check.messages?.length && check.message && <div className="mt-1">{check.message}</div>}
          </div>
        )}

        {/* Inhalt: Aenderungsliste (change) + Payload */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading ? (
            <div className={`text-center text-sm py-8 ${muted}`}>{t('common.loading')}</div>
          ) : !payload ? (
            <div className={`text-center text-sm py-8 ${muted}`}>—</div>
          ) : (
            <>
              {isChange && preview.changes.length > 0 && (
                <div>
                  <div className={`text-xs font-semibold mb-1.5 ${sectionTitle}`}>{t('material.updChanges')}</div>
                  <div className={`rounded-xl p-3 space-y-1.5 ${kvBox}`}>
                    {preview.changes.map((c, i) => (
                      <div key={i} className="text-sm">
                        <span className={`font-mono text-[10px] mr-2 ${isDarkMode ? 'text-white/35' : 'text-slate-400'}`}>{c.struct}.{c.field}</span>
                        <span className={`line-through mr-2 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{c.from === '' ? t('material.trfEmpty') : c.from}</span>
                        <span className={isDarkMode ? 'text-white/90' : 'text-slate-800'}>{c.to === '' ? t('material.trfEmpty') : c.to}</span>
                        {c.note && <div className={`text-[11px] mt-0.5 ${isDarkMode ? 'text-amber-300/80' : 'text-amber-600'}`}>{c.note}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {DATA_STRUCTS.filter((s) => payload[s] && Object.keys(payload[s]).length).map((s) => (
                <div key={s}>
                  <div className={`text-xs font-semibold mb-1.5 ${sectionTitle}`}>{s}</div>
                  <div className={`rounded-xl p-3 ${kvBox}`}>{renderKV(payload[s])}</div>
                </div>
              ))}
              {Array.isArray(payload.MATERIALDESCRIPTION) && (
                <div>
                  <div className={`text-xs font-semibold mb-1.5 ${sectionTitle}`}>MATERIALDESCRIPTION</div>
                  <div className={`rounded-xl p-3 space-y-1 ${kvBox}`}>
                    {payload.MATERIALDESCRIPTION.map((d, i) => (
                      <div key={i} className={`text-sm ${isDarkMode ? 'text-white/85' : 'text-slate-700'}`}>
                        <span className="font-mono text-[10px] mr-2">{d.LANGU_ISO || d.LANGU}{d.DEL_FLAG ? ' (del)' : ''}</span>{d.MATL_DESC}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {Array.isArray(payload.MATERIALLONGTEXT) && (
                <div>
                  <div className={`text-xs font-semibold mb-1.5 ${sectionTitle}`}>MATERIALLONGTEXT</div>
                  <div className={`rounded-xl p-3 space-y-0.5 ${kvBox}`}>
                    {payload.MATERIALLONGTEXT.map((l, i) => (
                      <div key={i} className={`text-sm ${isDarkMode ? 'text-white/85' : 'text-slate-700'}`}>
                        <span className="font-mono text-[10px] mr-2">{l.TEXT_ID}/{l.LANGU_ISO || l.LANGU} {l.FORMAT_COL || '·'}</span>{l.TEXT_LINE}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {Array.isArray(payload.UNITSOFMEASURE) && (
                <div>
                  <div className={`text-xs font-semibold mb-1.5 ${sectionTitle}`}>UNITSOFMEASURE</div>
                  <div className={`rounded-xl p-3 space-y-0.5 ${kvBox}`}>
                    {payload.UNITSOFMEASURE.map((u, i) => (
                      <div key={i} className={`text-sm ${isDarkMode ? 'text-white/85' : 'text-slate-700'}`}>
                        <span className="font-mono text-[10px] mr-2">{u.ALT_UNIT}{u.DEL_FLAG ? ' (del)' : ''}</span>{u.NUMERATOR} / {u.DENOMINATR}
                        {Object.entries(u).filter(([k]) => !['ALT_UNIT', 'ALT_UNIT_ISO', 'NUMERATOR', 'DENOMINATR', 'DEL_FLAG'].includes(k)).map(([k, v]) => (
                          <span key={k} className={`ml-2 text-xs ${isDarkMode ? 'text-white/55' : 'text-slate-500'}`}><span className="font-mono text-[10px]">{k}</span> {v === '' ? t('material.trfEmpty') : String(v)}</span>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {payload._unmapped && Object.keys(payload._unmapped).length > 0 && (
                <div>
                  <div className="text-xs font-semibold mb-1.5 text-amber-500">{t('material.trfUnmapped')}</div>
                  <div className={`rounded-xl p-3 border border-amber-400/30 ${isDarkMode ? 'bg-amber-500/5' : 'bg-amber-50/50'}`}>{renderKV(payload._unmapped)}</div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Aktionen */}
        <div className={`flex items-center justify-end gap-3 p-4 border-t ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`}>
          <button type="button" onClick={onClose}
            className={`px-4 py-2 rounded-xl text-sm font-medium ${isDarkMode ? 'bg-white/10 hover:bg-white/15 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'}`}>
            {t('material.dupCancel')}
          </button>
          {target !== 'none' && (
            <button type="button" onClick={doCheck} disabled={!canWrite}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold disabled:opacity-50 ${isDarkMode ? 'bg-white/10 hover:bg-white/15 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'}`}>
              <ShieldCheck className="w-4 h-4" /> {t(isChange ? 'material.updCheck' : 'material.trfCheck')}
            </button>
          )}
          <button type="button" onClick={doTransfer} disabled={!canWrite}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">
            {isChange ? <RefreshCw className="w-4 h-4" /> : <Send className="w-4 h-4" />} {t(isChange ? 'material.updConfirm' : 'material.trfConfirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransferDialog;
